import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Checkbox } from "../components/ui/checkbox";
import { Label } from "../components/ui/label";
import { toast } from "sonner";

function makeSessionId() {
  return `sess-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

export default function TalentPoolStageTwoAssessment() {
  const nav = useNavigate();
  const [loading, setLoading] = useState(true);
  const [overview, setOverview] = useState(null);
  const [consentChecked, setConsentChecked] = useState(false);
  const [practice, setPractice] = useState(null);
  const [practiceDone, setPracticeDone] = useState(false);
  const [attemptState, setAttemptState] = useState(null);
  const [question, setQuestion] = useState(null);
  const [timeLeft, setTimeLeft] = useState(30);
  const [answering, setAnswering] = useState(false);
  const [completed, setCompleted] = useState(false);
  const [sessionId] = useState(() => makeSessionId());
  const questionShownAtRef = useRef(null);

  const warningClass = useMemo(() => {
    if (timeLeft <= 5) return "border-red-300 bg-red-50 text-red-900";
    if (timeLeft <= 10) return "border-amber/40 bg-amber/10 text-ink";
    return "border-border bg-paper text-ink";
  }, [timeLeft]);

  const refreshOverview = async () => {
    const r = await api.get("/talent-pool/stage-2/overview");
    setOverview(r.data || null);
    if (r.data?.attempt?.status === "completed") {
      setCompleted(true);
    }
  };

  const loadCurrentQuestion = async () => {
    try {
      const r = await api.get("/talent-pool/stage-2/current-question", {
        params: { client_session_id: sessionId },
      });
      if (r.data?.completed) {
        setCompleted(true);
        setQuestion(null);
        await refreshOverview();
        return;
      }
      setQuestion(r.data?.question || null);
      questionShownAtRef.current = Date.now();
      if (r.data?.question?.expires_at) {
        const expiresAt = new Date(r.data.question.expires_at).getTime();
        const now = Date.now();
        setTimeLeft(Math.max(0, Math.ceil((expiresAt - now) / 1000)));
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not load question");
    }
  };

  useEffect(() => {
    (async () => {
      try {
        await refreshOverview();
      } catch (err) {
        toast.error(err.response?.data?.detail || "Could not load Stage 2");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  useEffect(() => {
    if (!question?.expires_at || completed) return;
    const timer = setInterval(() => {
      const expiresAt = new Date(question.expires_at).getTime();
      const remaining = Math.max(0, Math.ceil((expiresAt - Date.now()) / 1000));
      setTimeLeft(remaining);
      if (remaining === 0) {
        clearInterval(timer);
        loadCurrentQuestion();
      }
    }, 250);
    return () => clearInterval(timer);
  }, [question?.expires_at, completed]);

  useEffect(() => {
    const onVisibility = async () => {
      try {
        await api.post("/talent-pool/stage-2/heartbeat", {
          client_session_id: sessionId,
          visible: !document.hidden,
          interruption_reason: document.hidden ? "visibility_hidden" : "visibility_visible",
        });
      } catch {
        // heartbeat is best-effort
      }
    };
    document.addEventListener("visibilitychange", onVisibility);
    return () => document.removeEventListener("visibilitychange", onVisibility);
  }, [sessionId]);

  const submitConsent = async () => {
    if (!consentChecked) {
      toast.error("Please confirm that you understand the instructions.");
      return;
    }
    try {
      await api.post("/talent-pool/stage-2/consent", { understood: true });
      await refreshOverview();
      toast.success("Confirmation recorded");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not record confirmation");
    }
  };

  const loadPractice = async () => {
    try {
      const r = await api.get("/talent-pool/stage-2/practice-question");
      setPractice(r.data || null);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not load practice question");
    }
  };

  const answerPractice = async (letter) => {
    try {
      await api.post("/talent-pool/stage-2/practice-answer", { selected_option: letter });
      setPracticeDone(true);
      setPractice(null);
      await refreshOverview();
      toast.success("Practice complete");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not submit practice response");
    }
  };

  const beginScored = async () => {
    try {
      const r = await api.post("/talent-pool/stage-2/start", { client_session_id: sessionId });
      setAttemptState(r.data?.attempt || null);
      setCompleted(false);
      await loadCurrentQuestion();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not start assessment");
    }
  };

  const submitAnswer = async (optToken) => {
    if (!question?.question_token || answering) return;
    setAnswering(true);
    try {
      const elapsedMs = questionShownAtRef.current ? Date.now() - questionShownAtRef.current : null;
      const r = await api.post("/talent-pool/stage-2/answer", {
        question_token: question.question_token,
        option_token: optToken,
        client_response_ms: elapsedMs,
        client_question_started_at: question.server_issued_at,
        client_session_id: sessionId,
      });
      if (r.data?.completed) {
        setCompleted(true);
        setQuestion(null);
        await refreshOverview();
        return;
      }
      await loadCurrentQuestion();
    } catch (err) {
      if (err.response?.status === 409) {
        await loadCurrentQuestion();
      } else {
        toast.error(err.response?.data?.detail || "Could not submit response");
      }
    } finally {
      setAnswering(false);
    }
  };

  const canOpenPractice = Boolean(overview?.understanding_confirmed) && !practiceDone;

  if (loading) {
    return <div className="min-h-screen bg-sand p-8 text-ink">Loading Stage 2 assessment...</div>;
  }

  return (
    <div className="min-h-screen bg-sand">
      <header className="bg-navy text-bone">
        <div className="livery-stripe h-1" />
        <div className="max-w-4xl mx-auto px-6 md:px-10 h-16 flex items-center justify-between">
          <Link to="/talent-pool/dashboard" className="font-display font-bold text-sm">Railway Careers UK · Talent Pool</Link>
          <Button variant="outline" className="border-bone/30 text-bone hover:bg-cream/10" onClick={() => nav("/talent-pool/dashboard")}>Back to dashboard</Button>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 md:px-10 py-8">
        <h1 className="font-display font-extrabold text-ink text-3xl">Operational Behaviour Assessment</h1>

        {completed ? (
          <div className="mt-6 rounded-lg border border-border bg-card p-6" data-testid="stage2-completed">
            <h2 className="font-display font-bold text-xl text-ink">Stage 2 assessment completed</h2>
            <p className="mt-2 text-muted-foreground">Your responses have been recorded and will be considered as part of the talent-pool progression process.</p>
            <Button className="mt-5 bg-navy text-bone hover:bg-lime-600 hover:text-ink" onClick={() => nav("/talent-pool/dashboard")}>Return to dashboard</Button>
          </div>
        ) : null}

        {!completed && !question && (
          <div className="mt-6 space-y-5">
            <section className="rounded-lg border border-border bg-card p-6">
              <h2 className="font-semibold text-ink">Before you start</h2>
              <ul className="mt-3 text-sm text-muted-foreground space-y-1 list-disc list-inside">
                <li>This assessment is timed.</li>
                <li>Each question can only be viewed once.</li>
                <li>Previous questions cannot be revisited.</li>
                <li>Unanswered questions will automatically advance.</li>
                <li>The assessment may normally only be attempted once.</li>
                <li>Your responses will be used to determine progression.</li>
                <li>Detailed scoring information is not shown.</li>
                <li>Technical problems must be reported to the support team.</li>
                <li>Reasonable adjustments must be requested before starting.</li>
              </ul>

              <div className="mt-4 flex items-start gap-3">
                <Checkbox id="stage2-understood" checked={consentChecked} onCheckedChange={(v) => setConsentChecked(Boolean(v))} />
                <div>
                  <Label htmlFor="stage2-understood" className="text-ink font-semibold">I understand the Stage 2 assessment instructions.</Label>
                </div>
              </div>

              <Button className="mt-4 bg-navy text-bone hover:bg-lime-600 hover:text-ink" onClick={submitConsent} disabled={overview?.understanding_confirmed}>
                {overview?.understanding_confirmed ? "Understanding confirmed" : "Confirm and continue"}
              </Button>
            </section>

            <section className="rounded-lg border border-border bg-card p-6">
              <h2 className="font-semibold text-ink">Practice question</h2>
              <p className="mt-2 text-sm text-muted-foreground">This practice question is untimed and does not contribute to your scored attempt.</p>

              {!practice && !practiceDone && (
                <Button className="mt-4 bg-amber text-ink hover:bg-amber-600" onClick={loadPractice} disabled={!canOpenPractice}>
                  Open practice question
                </Button>
              )}

              {practiceDone && (
                <div className="mt-4 rounded-md border border-lime-300 bg-lime-50 px-3 py-2 text-sm text-lime-900">Practice question completed.</div>
              )}

              {practice && (
                <div className="mt-4 rounded-md border border-border bg-paper p-4">
                  <p className="text-ink text-sm leading-6">{practice.scenario}</p>
                  <div className="mt-4 grid gap-2">
                    {(practice.options || []).map((opt) => (
                      <button
                        key={opt.letter}
                        type="button"
                        className="text-left rounded-md border border-border bg-background px-3 py-2 text-sm hover:bg-sky/10 focus:outline-none focus:ring-2 focus:ring-sky/50"
                        onClick={() => answerPractice(opt.letter)}
                      >
                        <span className="font-semibold mr-2">{opt.letter}.</span>{opt.text}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </section>

            <section className="rounded-lg border border-border bg-card p-6">
              <h2 className="font-semibold text-ink">Scored assessment</h2>
              <p className="mt-2 text-sm text-muted-foreground">The formal attempt begins only when you select Begin scored assessment.</p>
              <Button className="mt-4 bg-navy text-bone hover:bg-lime-600 hover:text-ink" onClick={beginScored} disabled={!overview?.understanding_confirmed || (!overview?.practice_completed && !practiceDone)}>
                Begin scored assessment
              </Button>
            </section>
          </div>
        )}

        {!completed && question && (
          <section className="mt-6 rounded-lg border border-border bg-card p-6" data-testid="stage2-live-question">
            <div className="flex items-center justify-between gap-3">
              <div className="text-sm text-muted-foreground">Question {question.question_position} of {question.question_count}</div>
              <div className={`rounded-full border px-3 py-1 text-sm font-semibold ${warningClass}`} aria-live="polite">
                {timeLeft}s remaining
              </div>
            </div>

            <p className="mt-4 text-ink leading-6">{question.scenario}</p>

            <div className="mt-4 grid gap-2" role="radiogroup" aria-label="Response options">
              {(question.options || []).map((opt) => (
                <button
                  key={opt.option_token}
                  type="button"
                  className="text-left rounded-md border border-border bg-background px-3 py-2 text-sm hover:bg-sky/10 focus:outline-none focus:ring-2 focus:ring-sky/50 disabled:opacity-60"
                  onClick={() => submitAnswer(opt.option_token)}
                  disabled={answering}
                >
                  <span className="font-semibold mr-2">{opt.letter}.</span>{opt.text}
                </button>
              ))}
            </div>
          </section>
        )}
      </main>
    </div>
  );
}
